import type { ColumnDef, RowData } from '@tanstack/react-table'
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { tw } from 'utils/classnames'

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    nowrap?: boolean
    headerClassName?: string
    cellClassName?: string
  }
}

// https://github.com/TanStack/table/issues/4302
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GenericColumnDef<T> = ColumnDef<T, any>

export function Table<T>({
  data,
  columns,
  className,
  emptyPlaceholder,
}: {
  data: Partial<T>[]
  columns: GenericColumnDef<T>[]
  className?: string
  emptyPlaceholder?: React.ReactNode
}) {
  // eslint-disable-next-line
  const table = useReactTable<T | any>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })
  if (table.getRowModel().rows.length === 0 && emptyPlaceholder) {
    return emptyPlaceholder
  }
  return (
    <table className={className}>
      <thead>
        {table.getHeaderGroups().map(headerGroup => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header, index) => (
              <th
                key={header.id}
                className={tw(
                  'bg-cf-grey-50 text-cf-grey-1000 whitespace-nowrap px-3 py-1.5 text-left text-sm font-medium',
                  index === 0 && 'rounded-l',
                  index === headerGroup.headers.length - 1 && 'rounded-r',
                  header.column.columnDef.meta?.headerClassName,
                )}
                style={{ width: `${header.getSize()}px` }}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map(row => (
          <tr key={row.id} className="border-cf-grey-200 border-b">
            {row.getVisibleCells().map(cell => (
              <td
                key={cell.id}
                className={tw(
                  'text-14 px-3 py-2',
                  cell.column.columnDef.meta?.nowrap ? 'whitespace-nowrap' : '',
                  cell.column.columnDef.meta?.cellClassName,
                )}
                style={{ width: `${cell.column.getSize()}px` }}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
